import { createContext, useContext, useEffect, useState, startTransition } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    type User,
    onAuthStateChanged,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth, googleProvider, isFirebaseConfigured } from '../firebase';
import { logAnalyticsIdentify } from '../analytics/nativeAnalytics';
import { clearRequestsCache } from '../spaces/people-space/views/taActionBoard/requestsCache';
import { resetRemindersStore } from '../spaces/people-space/views/taActionBoard/useReminders';
import { resetScheduledSendsStore } from '../spaces/people-space/views/taActionBoard/useScheduledSends';

/**
 * Why an error KIND and not just a string: LoginView rendered the same
 * "Access Denied — restricted to @finn.com accounts" copy for every `authError`,
 * so a recruiter whose sign-in popup was blocked was told they were not
 * authorised. That is a lie they cannot act on, and it hid the real cause during
 * the TA Action Board rollout (Aug 2026) — the reports came in as "it says I'm
 * not allowed", which sent everyone looking at permissions instead of the popup.
 * Only `domain` means "you may not come in"; `technical` means "sign-in broke".
 */
export type AuthErrorKind = 'domain' | 'technical';

export interface AuthErrorState {
    kind: AuthErrorKind;
    message: string;
    /** Firebase `auth/*` code, surfaced so the next report is diagnosable. */
    code?: string;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    signIn: () => Promise<void>;
    signOut: () => Promise<void>;
    isConfigured: boolean;
    authError: AuthErrorState | null;
}

const ALLOWED_DOMAIN = 'finn.com';

/**
 * The popup was dismissed or superseded — the user's own doing, not a failure.
 * `cancelled-popup-request` in particular means a SECOND sign-in click replaced
 * the first, so falling back to a redirect here would navigate the page away on
 * an ordinary double-click.
 */
const SILENT_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request']);

/** The popup never opened. A full-page redirect needs no popup permission. */
const REDIRECT_FALLBACK_CODES = new Set([
    'auth/popup-blocked',
    'auth/operation-not-supported-in-this-environment',
]);

/**
 * The redirect resolver must never hang the app on a spinner. If the auth-domain
 * iframe is slow or blocked outright, show the sign-in button anyway — a visible
 * button beats an indefinite loading state.
 */
const REDIRECT_RESULT_TIMEOUT_MS = 4000;

const isAllowedEmail = (email: string | null | undefined) =>
    (email?.toLowerCase() ?? '').endsWith(`@${ALLOWED_DOMAIN}`);

const domainError = (): AuthErrorState => ({
    kind: 'domain',
    message: `Access restricted to @${ALLOWED_DOMAIN} email addresses.`,
});

const errorCode = (error: unknown): string =>
    typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code: unknown }).code)
        : '';

/** Maps a Firebase failure onto copy that names something the user can act on. */
function technicalError(error: unknown): AuthErrorState {
    const code = errorCode(error);
    const message = (() => {
        switch (code) {
            case 'auth/popup-blocked':
                return 'Your browser blocked the sign-in window and the redirect fallback could not start. Allow pop-ups for this site, then try again.';
            case 'auth/unauthorized-domain':
                return 'This address is not authorised for sign-in. Open https://people-space.finn.com instead.';
            case 'auth/network-request-failed':
                return 'Could not reach Google. Check your connection or VPN, then try again.';
            case 'auth/web-storage-unsupported':
                return 'Your browser is blocking the storage sign-in needs. Allow cookies for this site, or use Chrome.';
            case 'auth/too-many-requests':
                return 'Too many sign-in attempts. Wait a minute, then try again.';
            default:
                return 'Sign-in failed. Please try again.';
        }
    })();
    return { kind: 'technical', message, code: code || undefined };
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const queryClient = useQueryClient();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState<AuthErrorState | null>(null);
    const isConfigured = isFirebaseConfigured();

    useEffect(() => {
        if (!isConfigured) {
            startTransition(() => setLoading(false));
            return;
        }

        let cancelled = false;
        let redirectSettled = false;
        let authSettled = false;
        const settle = () => {
            if (!cancelled && redirectSettled && authSettled) {
                startTransition(() => setLoading(false));
            }
        };
        const settleRedirect = () => {
            redirectSettled = true;
            settle();
        };

        // Two jobs in one call. It completes a sign-in that came back via
        // `signInWithRedirect`, AND it forces the redirect resolver — the
        // auth-domain iframe — to load NOW rather than on the first click. That
        // warm-up is what keeps the POPUP path working on a cold visit: with a
        // cold resolver, `signInWithPopup` awaits the iframe before it calls
        // window.open, and by then the browser no longer counts the open as
        // user-initiated and blocks it. It is why the rollout failed for
        // first-time visitors while working for everyone who had used the app
        // before. Do not make this lazy.
        const timeout = setTimeout(settleRedirect, REDIRECT_RESULT_TIMEOUT_MS);
        getRedirectResult(auth)
            .then((result) => {
                // The @finn.com gate itself lives in onAuthStateChanged below,
                // which fires for the redirect path too. Signing a rejected
                // account out here as well only shortens the window in which it
                // holds a session.
                if (!cancelled && result?.user && !isAllowedEmail(result.user.email)) {
                    void firebaseSignOut(auth);
                }
            })
            .catch((error) => {
                if (!cancelled) setAuthError(technicalError(error));
            })
            .finally(() => {
                clearTimeout(timeout);
                settleRedirect();
            });

        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            // Salary ranges are user-scoped and can remain fresh for five minutes. A shared
            // browser must never render the previous colleague's cached response after an
            // account switch, so remove this whole query family before exposing the new user.
            queryClient.removeQueries({ queryKey: ['salary-bands'] });
            if (firebaseUser) {
                const email = firebaseUser.email?.toLowerCase() ?? '';
                if (isAllowedEmail(email)) {
                    setUser(firebaseUser);
                    setAuthError(null);
                    // Amplitude events group by the signed-in FINN user (never candidate data).
                    logAnalyticsIdentify(email);
                } else {
                    void firebaseSignOut(auth);
                    setUser(null);
                    logAnalyticsIdentify(null);
                    setAuthError(domainError());
                }
            } else {
                setUser(null);
                // A shared browser must not keep attributing events to the previous user.
                logAnalyticsIdentify(null);
                // Nor hold on to their candidate mail: signing out does not reload the page, so the
                // Candidate requests session cache has to be dropped explicitly. Here rather than in
                // signOut() below, so it covers every way an account can go away.
                clearRequestsCache();
                // Same rule for the reminders store: its titles name candidates, and a 'ready'
                // store would also skip the load for the NEXT account and show them stale rows.
                resetRemindersStore();
                // And the queued replies: a row names a candidate, and a leftover 'ready' store
                // would show the next account somebody else's scheduled sends.
                resetScheduledSendsStore();
            }
            authSettled = true;
            settle();
        });

        return () => {
            cancelled = true;
            clearTimeout(timeout);
            unsubscribe();
        };
    }, [isConfigured, queryClient]);

    const signIn = async () => {
        if (!isConfigured) return;
        setAuthError(null);
        try {
            const result = await signInWithPopup(auth, googleProvider);
            if (result.user && !isAllowedEmail(result.user.email)) {
                await firebaseSignOut(auth);
                setAuthError(domainError());
            }
        } catch (error) {
            const code = errorCode(error);
            if (SILENT_CODES.has(code)) return;

            if (REDIRECT_FALLBACK_CODES.has(code)) {
                try {
                    // Navigates away; nothing after this runs when it succeeds.
                    await signInWithRedirect(auth, googleProvider);
                    return;
                } catch (redirectError) {
                    console.error('Error signing in via redirect', redirectError);
                    // Report whichever failure the user can act on. A redirect
                    // refused by third-party-storage rules says nothing useful;
                    // the blocked popup at least names the pop-up setting.
                    setAuthError(
                        errorCode(redirectError)
                            ? technicalError(redirectError)
                            : technicalError(error)
                    );
                    return;
                }
            }

            console.error('Error signing in', error);
            setAuthError(technicalError(error));
        }
    };

    const signOut = async () => {
        if (!isConfigured) return;
        try {
            await firebaseSignOut(auth);
            setAuthError(null);
        } catch (error) {
            console.error('Error signing out', error);
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, signIn, signOut, isConfigured, authError }}>
            {children}
        </AuthContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};
